import { GridSortItem } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import LoadingOverlay from "../../../../../components/LoadingOverlay";
import RenderWithTooltip from "../../../../../components/RenderWithTooltip";
import Text from "../../../../../components/Text";
import ActionMenu from "../../../../../components/app/ActionMenu";
import { useRoleController } from "../../../../../components/useRoleController";
import { PROVIDER_PATHS } from "../../../../../constants/paths";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { useGetEras } from "../../../../../queries/billings/era/use-get-eras";
import { formatDate } from "../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION_SMALL,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getValueFromObj,
  replaceUnderScoreAddSpace,
  roleFilter,
} from "../../../../../utils/utils";
import UploadEraEobModal from "./UploadEraEobModal";
import ViewEraEob from "./ViewEraEob";
import { EraEobChildContext } from "./EraEobTab";

type EraClaim = {
  claimId?: string;
  claimStatus?: string;
  claimType?: string;
  patientName?: string;
  chargeAmount?: number;
  allowedAmount?: number;
  insurancePaid?: number;
  patientResponsibility?: number;
  insuranceAdjustment?: number;
  patientProcedureCodes?: Array<Record<string, unknown>>;
};

type EraDto = {
  uuid?: string;
  eraDocNumber?: string;
  eraDate?: string;
  paymentMethod?: string;
  reconciled?: boolean;
  payerName?: string;
  billingProviderName?: string;
  paymentDate?: string;
  claims?: EraClaim[];
  pendingClaims?: EraClaim[];
  billingProvider?: any;
  payer?: any;
};

type EraWorklistRow = {
  claims: EraClaim[];
  uuid: string;
  eraDocNo: string;
  eraStatementDate: string;
  payerName: any;
  patientName: string;
  paymentIssueDate: string;
  claimType: string;
  billingProvider: any;
  paymentMethod: string;
  posted: boolean;
  totalBilledAmount: number;
  totalAllowedAmount: number;
  totalPaidAmount: number;
};

const EraWorklist = () => {
  const navigate = useNavigate();
  const { filter, debouncedSearchKey, onSummaryCountsChange } =
    useOutletContext<EraEobChildContext>();

  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const [pagination, setPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION_SMALL);
  const [downloadEraUuid, setDownloadEraUuid] = useState<string>("");

  const { hasView: hasViewEraEob } = useRoleController("Generate Claims");
  const { hasUpdate: hasUpdateEraEob } = useRoleController("Generate Claims");
  const { hasUpdate: hasPostPayment } = useRoleController("Generate Claims");
  const { hasUpdate: hasWriteOffPayment } =
    useRoleController("Generate Claims");
  const { hasUpdate: hasVoidPayment } = useRoleController("Generate Claims");

  const viewEraEobDisclosure = useDisclosure();
  const uploadEraEobDisclosure = useDisclosure();

  const eraDateFrom =
    filter?.date_range === "CUSTOM_RANGE" && filter?.startDate
      ? dayjs(filter.startDate).startOf("day").toISOString()
      : undefined;
  const eraDateTo =
    filter?.date_range === "CUSTOM_RANGE" && filter?.endDate
      ? dayjs(filter.endDate).endOf("day").toISOString()
      : undefined;

  const {
    data: eraData,
    isLoading,
    isFetching,
  } = useGetEras({
    pagination,
    gridSorting,
    billingProviderId: filter?.providers?.[0]?.value,
    eraDateFrom,
    eraDateTo,
    searchKey: debouncedSearchKey,
    // isManualUpload: false,
  });

  const content = (eraData?.data?.content ?? []) as EraDto[];

  const formatCurrency = (amount: number) => `$ ${amount.toFixed(2)}`;

  const goToEraDetail = (uuid: string) => {
    if (!uuid) return;
    navigate(`${PROVIDER_PATHS.ERA_EOB_DETAIL}/${uuid}`);
  };

  const rows = useMemo<EraWorklistRow[]>(() => {
    const sumBy = (claims: EraClaim[], key: keyof EraClaim) =>
      claims.reduce((acc, claim) => acc + Number(claim?.[key] ?? 0), 0);

    return content.map((era) => {
      // ECRH-5170 — before any CPT is posted the backend returns the claims in
      // `pendingClaims` and leaves `claims` as an empty array, so a plain `??`
      // never falls back. Use the populated list so amounts show pre-posting.
      const claims =
        (era?.claims?.length ? era?.claims : era?.pendingClaims) ?? [];
      const firstClaim = claims[0];

      const patientName =
        Array.from(
          new Set(claims.map((c) => c?.patientName).filter(Boolean))
        ).join(", ") || "-";

      return {
        ...era,
        claims,
        uuid: era?.uuid ?? "",
        eraDocNo:
          era?.eraDocNumber ?? era?.uuid?.split("-")?.[0]?.toUpperCase() ?? "-",
        eraStatementDate: era?.eraDate ?? "",
        payerName: era?.payer ? getValueFromObj(era?.payer) : "-",
        patientName,
        paymentIssueDate: era?.paymentDate ?? era?.eraDate ?? "",
        claimType:
          firstClaim?.claimType === "electronic_claim"
            ? "Electronic"
            : firstClaim?.claimType
              ? "Paper"
              : "-",
        billingProvider: era?.billingProvider
          ? getValueFromObj(era?.billingProvider)
          : "-",
        paymentMethod: era?.paymentMethod ?? "-",
        posted: !!(era as any)?.posted,
        totalBilledAmount: sumBy(claims, "chargeAmount"),
        totalAllowedAmount: sumBy(claims, "allowedAmount"),
        totalPaidAmount: sumBy(claims, "insurancePaid"),
      };
    });
  }, [content]);

  // Update summary counts for parent
  useEffect(() => {
    const allClaims = content.flatMap((era) => era?.claims ?? []);
    const totalEobs = allClaims.length;
    const mapped = allClaims.filter((claim) => !!claim?.claimId).length;
    onSummaryCountsChange({
      numberOfERAs:
        Number(eraData?.data?.page?.totalElements ?? content.length) || 0,
      numberOfEOBs: totalEobs,
      numberOfMappedEOBs: mapped,
      numberOfUnmappedEOBs: Math.max(totalEobs - mapped, 0),
      numberOfSuccessfulPayments: content.filter((era) => !!era?.reconciled)
        .length,
      numberOfPaymentFailures: content.filter((era) => !era?.reconciled).length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, eraData?.data?.page?.totalElements]);

  const COLUMNS = applyDefaultColumnSettings<EraWorklistRow>([
    {
      field: "eraDocNo",
      flex: 0.6,
      minWidth: 120,
      headerName: "ERA Doc No",
      sortable: false,
      renderCell: ({ row }: { row: EraWorklistRow }) => (
        <Text
          isTabletext
          sx={{ color: "#2C57B3", cursor: "pointer", fontWeight: 500 }}
          onClick={() => goToEraDetail(row.uuid)}
        >
          {row?.eraDocNo || "-"}
        </Text>
      ),
    },
    {
      field: "eraStatementDate",
      flex: 0.6,
      minWidth: 140,
      headerName: "ERA Statement Date",
      sortable: false,
      renderCell: ({ row }: { row: EraWorklistRow }) => (
        <Text isTabletext>{formatDate(row?.eraStatementDate) ?? "-"}</Text>
      ),
    },
    {
      field: "payerName",
      flex: 0.7,
      minWidth: 150,
      headerName: "Payer Name",
      sortable: false,
      renderCell: ({ row }: { row: EraWorklistRow }) => (
        <Text isTabletext>{row?.payerName || "-"}</Text>
      ),
    },
    {
      field: "patientName",
      flex: 0.7,
      minWidth: 150,
      headerName: "Patient Name",
      sortable: false,
      // A batch ERA carries one name per claim, joined into a single cell, so
      // the list gets cut off. RenderWithTooltip shows the full list on hover
      // and only when the text is actually truncated.
      renderCell: ({ row }: { row: EraWorklistRow }) => (
        <RenderWithTooltip value={row?.patientName || "-"} />
      ),
    },
    {
      field: "paymentIssueDate",
      flex: 0.6,
      minWidth: 140,
      headerName: "Payment Issue Date",
      sortable: false,
      renderCell: ({ row }: { row: EraWorklistRow }) => (
        <Text isTabletext>{formatDate(row?.paymentIssueDate) ?? "-"}</Text>
      ),
    },
    {
      field: "claimType",
      flex: 0.5,
      minWidth: 120,
      headerName: "Claim Type",
      sortable: false,
      renderCell: ({ row }: { row: EraWorklistRow }) => (
        <Text isTabletext>{row?.claimType || "-"}</Text>
      ),
    },
    {
      field: "billingProvider",
      flex: 0.7,
      minWidth: 150,
      headerName: "Billing Provider",
      sortable: false,
      renderCell: ({ row }: { row: EraWorklistRow }) => (
        <Text isTabletext>{row?.billingProvider || "-"}</Text>
      ),
    },
    {
      field: "paymentMethod",
      flex: 0.6,
      minWidth: 130,
      headerName: "Payment Method",
      sortable: false,
      renderCell: ({ row }: { row: EraWorklistRow }) => (
        <Text isTabletext>
          {replaceUnderScoreAddSpace(row?.paymentMethod) || "-"}
        </Text>
      ),
    },
    {
      field: "postedStatus",
      flex: 0.6,
      minWidth: 130,
      headerName: "Posting Status",
      sortable: false,
      renderCell: ({ value }) => <Text isTabletext>{value}</Text>,
    },
    {
      field: "totalBilledAmount",
      flex: 0.6,
      minWidth: 150,
      headerName: "Total Billed Amount",
      sortable: false,
      renderCell: ({ row }: { row: EraWorklistRow }) => (
        <Text isTabletext>{formatCurrency(row?.totalBilledAmount || 0)}</Text>
      ),
    },
    {
      field: "totalAllowedAmount",
      flex: 0.6,
      minWidth: 160,
      headerName: "Total Allowed amount",
      sortable: false,
      renderCell: ({ row }: { row: EraWorklistRow }) => (
        <Text isTabletext>{formatCurrency(row?.totalAllowedAmount || 0)}</Text>
      ),
    },
    {
      field: "totalPaidAmount",
      flex: 0.6,
      minWidth: 150,
      headerName: "Total Paid amount",
      sortable: false,
      renderCell: ({ row }: { row: EraWorklistRow }) => (
        <Text isTabletext>{formatCurrency(row?.totalPaidAmount || 0)}</Text>
      ),
    },
    {
      field: "Action",
      width: 90,
      hideSortIcons: true,
      renderCell: ({ row }: { row: EraWorklistRow }) => {
        return (
          <ActionMenu
            options={roleFilter({
              list: [
                { value: "view_remittance", label: "View Remittance" },
                // { value: "view_era_eob", label: "View ERA/EOB" },
                // {
                //   value: "view_claim_details",
                //   label: "View Claim Details",
                //   isDisabled: !(row?.claims?.length > 0),
                //   hoverMsg: "No claims on this ERA",
                // },
                // { value: "post_payment", label: "Post Payment" },
                // { value: "download_era", label: "Download ERA" },
                // {
                //   value: "write_off_payment",
                //   label: "Write off Payment Post",
                // },
                // { value: "void_payment", label: "Void Payment Post" },
                // { value: "upload_era_eob", label: "Upload ERA/EOB" },
              ],
              compWith: {
                view_era_eob: hasViewEraEob,
                view_claim_details: hasViewEraEob,
                post_payment: hasPostPayment,
                download_era: hasViewEraEob,
                write_off_payment: hasWriteOffPayment,
                void_payment: hasVoidPayment,
                upload_era_eob: hasUpdateEraEob,
              },
            })}
            onActionClick={(v: string) => {
              if (v === "view_remittance") {
                viewEraEobDisclosure.open(row?.uuid);
              } else if (v === "view_era_eob") {
                viewEraEobDisclosure.open(row?.uuid);
              } else if (v === "view_claim_details") {
                goToEraDetail(row.uuid);
              } else if (v === "post_payment") {
                goToEraDetail(row.uuid);
              } else if (v === "download_era") {
                setDownloadEraUuid(row.uuid);
              } else if (v === "upload_era_eob") {
                uploadEraEobDisclosure.open();
              }
            }}
          />
        );
      },
    },
  ]);

  return (
    <>
      <LoadingOverlay isLoading={!!downloadEraUuid} />

      <UploadEraEobModal disclosureHost={uploadEraEobDisclosure} />

      <ViewEraEob
        disclosureHost={viewEraEobDisclosure}
        eraEobUuid={viewEraEobDisclosure.data}
        title="ERA Details"
      />
      <ViewEraEob
        disclosureHost={{
          opened: false,
          open: () => undefined,
          close: () => undefined,
          data: undefined,
        }}
        eraEobUuid={
          downloadEraUuid
            ? { uuid: downloadEraUuid, autoDownload: true }
            : undefined
        }
        onAutoDownloadComplete={() => setDownloadEraUuid("")}
      />

      <CustomDataGridV2
        searchString={debouncedSearchKey}
        loading={isLoading || isFetching}
        rows={rows}
        getRowId={(row: EraWorklistRow) => row?.uuid}
        columns={COLUMNS}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
        paginationModel={pagination}
        rowCount={eraData?.data?.page?.totalElements ?? rows.length}
        sortModel={gridSorting}
      />
    </>
  );
};

export default EraWorklist;
